import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { TenantBqSyncRun } from "@nexus/contracts";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  Loader2,
  RefreshCw,
  TriangleAlert,
  User,
  XCircle,
} from "lucide-react";
import {
  Button,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { toTitleCase } from "@/lib/title-case";
import { SettingsField, SettingsRow } from "./shared/SettingsRow";
import {
  formatSyncDurationMs,
  isCatalogLeftUnchanged,
  isLatestRunFailure,
  isLatestRunIncomplete,
  latestSuccessfulRun,
  summarizeSyncRun,
} from "@/features/identity-hub/lib/bq-sync-display";

const SETTINGS_CONTROL_CLASS =
  "h-9 rounded-md border border-input-border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

const CHIP_CLASS =
  "rounded-md border border-border bg-muted px-2 py-1 text-[11px] font-medium leading-none text-foreground";

const RUN_DATE_FORMAT: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" };
const CATALOG_DATE_FORMAT: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

export interface OrgBigQuerySyncSectionProps {
  isSuperTenant: boolean;
  isLoadingAdminSettings: boolean;
  customerPartition: string;
  onCustomerPartitionChange: (value: string) => void;
  conversionsWeekly: number;
  conversionsMonthly: number;
  onConversionsWeeklyChange: (value: number) => void;
  onConversionsMonthlyChange: (value: number) => void;
  conversionsSaved: boolean;
  bqCatalogAsOfAt: string | null;
  /** Post a failed sync to #nexus-admin-alerts (production only). */
  isFailureSlackAlertEnabled: boolean;
  onFailureSlackAlertEnabledChange: (enabled: boolean) => void;
  /** Org members, used to name who triggered a manual sync. */
  members: UserItem[];
  productTypes: Array<{ name: string }>;
  skuProducts: Array<{ name: string; isFocused: boolean }>;
  syncRuns: TenantBqSyncRun[];
  syncRunsTotalCount: number;
  isLoadingSyncRuns: boolean;
  latestRun: TenantBqSyncRun | null;
  isSyncing: boolean;
  isSavingPartition: boolean;
  partitionIsEmpty: boolean;
  onTriggerSync: () => void;
}

function formatRunDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, RUN_DATE_FORMAT);
}

function StatusBadge({ status }: { status: TenantBqSyncRun["status"] }) {
  const { t } = useTranslation();
  const label = t(`iam.orgDrawer.settings.bqSync.status.${status.toLowerCase()}`, toTitleCase(status.toLowerCase()));
  const className =
    status === "SYNCED"
      ? "bg-success-subtle text-success-subtle-foreground"
      : status === "FAILED"
        ? "bg-destructive/10 text-destructive"
        : status === "PARTIAL"
          ? "bg-warning-subtle text-warning-subtle-foreground"
          : "bg-muted text-muted-foreground";
  return (
    <span className={cn("w-fit shrink-0 rounded-full px-1.5 py-[3px] text-[10px] font-medium leading-none", className)}>
      {label}
    </span>
  );
}

function MoreChip({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <span className="rounded-md bg-primary-subtle px-2 py-1 text-[11px] font-medium leading-none text-primary-subtle-foreground">
      {t("iam.orgDrawer.settings.moreProductsChip", "+{{count}} more", { count })}
    </span>
  );
}

export function OrgBigQuerySyncSection({
  isSuperTenant,
  isLoadingAdminSettings,
  customerPartition,
  onCustomerPartitionChange,
  conversionsWeekly,
  conversionsMonthly,
  onConversionsWeeklyChange,
  onConversionsMonthlyChange,
  conversionsSaved,
  bqCatalogAsOfAt,
  isFailureSlackAlertEnabled,
  onFailureSlackAlertEnabledChange,
  members,
  productTypes,
  skuProducts,
  syncRuns,
  syncRunsTotalCount,
  isLoadingSyncRuns,
  latestRun,
  isSyncing,
  isSavingPartition,
  partitionIsEmpty,
  onTriggerSync,
}: OrgBigQuerySyncSectionProps) {
  const { t } = useTranslation();
  const [errorDetailsOpen, setErrorDetailsOpen] = useState(false);

  const focusedSkus = skuProducts.filter((p) => p.isFocused);
  const unfocusedSkuCount = skuProducts.length - focusedSkus.length;
  const showFailure = isLatestRunFailure(latestRun);
  const showIncomplete = isLatestRunIncomplete(latestRun);
  const partitionError = showFailure && latestRun?.errorCode === "PARTITION_EMPTY";
  const lastSynced = latestSuccessfulRun(syncRuns);
  const isBusy = isSyncing || isSavingPartition;
  const isSyncDisabled = isSuperTenant || isBusy || partitionIsEmpty;

  const memberNameById = useMemo(() => new Map(members.map((m) => [m.id, m.displayName || m.email])), [members]);

  const syncButtonLabel = isSavingPartition
    ? t("iam.orgDrawer.settings.savingPartition", "Saving partition…")
    : isSyncing
      ? t("iam.orgDrawer.settings.syncing", "Syncing…")
      : t("iam.orgDrawer.settings.syncWithBq", "Sync with BQ");

  return (
    <SettingsRow
      data-testid="org-settings-row-bigquery"
      bordered={false}
      title={t("iam.orgDrawer.settings.settingsRowBigQueryTitle", "BigQuery sync")}
      hint={t(
        "iam.orgDrawer.settings.settingsRowBigQueryHint",
        "Pulls this tenant's products and industries from BigQuery, using the partition and conversion targets.",
      )}
    >
      <div className="flex flex-col gap-4 rounded-lg border border-border p-4">
        {showFailure && latestRun && (
          <div
            className="flex flex-col gap-2 rounded-md border border-destructive/25 bg-destructive/5 p-3"
            data-testid="org-bq-sync-failure-banner"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <XCircle className="mt-px size-4 shrink-0 text-destructive" aria-hidden />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="text-xs font-semibold text-destructive">
                  {t("iam.orgDrawer.settings.bqSync.lastSyncFailedAt", "Last sync failed · {{date}}", {
                    date: formatRunDate(latestRun.startedAt),
                  })}
                  {latestRun.trigger === "SCHEDULED"
                    ? ` (${t("iam.orgDrawer.settings.bqSync.triggerScheduled", "scheduled")})`
                    : ""}
                </p>
                {latestRun.errorMessage && (
                  <p className="text-[11px] leading-relaxed text-destructive/90">{latestRun.errorMessage}</p>
                )}
                {bqCatalogAsOfAt && (
                  <p className="text-[11px] text-destructive/90">
                    {t("iam.orgDrawer.settings.bqSync.leftAsTheyWere", "Products were left as they were on {{date}}.", {
                      date: new Date(bqCatalogAsOfAt).toLocaleDateString(undefined, CATALOG_DATE_FORMAT),
                    })}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 border-destructive/25 bg-background px-2.5 text-[11px] text-destructive hover:bg-destructive/5 hover:text-destructive"
                    disabled={isSyncDisabled}
                    onClick={onTriggerSync}
                  >
                    {isSyncing ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                    {t("iam.orgDrawer.settings.bqSync.retryNow", "Retry now")}
                  </Button>
                  {latestRun.errorCode && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-destructive underline underline-offset-2"
                      aria-expanded={errorDetailsOpen}
                      onClick={() => setErrorDetailsOpen((open) => !open)}
                    >
                      {t("iam.orgDrawer.settings.bqSync.viewErrorDetails", "View error details")}
                      {errorDetailsOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {errorDetailsOpen && latestRun.errorCode && (
              <pre className="overflow-x-auto rounded-md border border-border bg-background p-2 text-[10px] text-muted-foreground">
                {latestRun.errorCode}
                {latestRun.errorMessage ? `\n${latestRun.errorMessage}` : ""}
              </pre>
            )}
          </div>
        )}

        {showIncomplete && latestRun && (
          <div
            className="flex items-start gap-3 rounded-md border border-warning-border bg-warning-subtle p-3"
            data-testid="org-bq-sync-incomplete-banner"
            role="status"
          >
            <TriangleAlert className="mt-px size-4 shrink-0 text-warning-subtle-foreground" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-1 text-warning-subtle-foreground">
              <p className="text-xs font-semibold">
                {t("iam.orgDrawer.settings.bqSync.lastSyncIncompleteAt", "Last sync finished with gaps · {{date}}", {
                  date: formatRunDate(latestRun.startedAt),
                })}
              </p>
              {latestRun.errorMessage && <p className="text-[11px] leading-relaxed">{latestRun.errorMessage}</p>}
              {bqCatalogAsOfAt && isCatalogLeftUnchanged(latestRun) && (
                <p className="text-[11px]">
                  {t("iam.orgDrawer.settings.bqSync.leftAsTheyWere", "Products were left as they were on {{date}}.", {
                    date: new Date(bqCatalogAsOfAt).toLocaleDateString(undefined, CATALOG_DATE_FORMAT),
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_110px_110px]">
          <SettingsField
            label={t("iam.orgDrawer.settings.customerPartitionLabel", "Customer partition")}
            htmlFor="org-settings-customer-partition"
            error={
              partitionError
                ? t("iam.orgDrawer.settings.bqSync.partitionEmptyShort", "No rows today — check the partition name")
                : undefined
            }
          >
            {isLoadingAdminSettings ? (
              <div className="h-9 rounded-md border border-border bg-muted/40 animate-pulse" />
            ) : (
              <div className="relative">
                <input
                  id="org-settings-customer-partition"
                  type="text"
                  data-testid="org-settings-customer-partition"
                  placeholder={t("iam.orgDrawer.settings.customerPartitionPlaceholder", "e.g. Acme Corp")}
                  value={customerPartition}
                  onChange={(e) => onCustomerPartitionChange(e.target.value)}
                  disabled={isSuperTenant}
                  aria-invalid={partitionError || undefined}
                  className={cn(
                    SETTINGS_CONTROL_CLASS,
                    "w-full font-mono text-xs",
                    partitionError && "border-destructive pr-8 focus:border-destructive focus:ring-destructive/40",
                  )}
                />
                {partitionError && (
                  <TriangleAlert
                    className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-destructive"
                    aria-hidden
                  />
                )}
              </div>
            )}
          </SettingsField>
          <SettingsField
            label={t("iam.orgDrawer.settings.weeklyConversionsShortLabel", "Weekly conv.")}
            htmlFor="org-settings-conversions-weekly"
          >
            {isLoadingAdminSettings ? (
              <div className="h-9 rounded-md border border-border bg-muted/40 animate-pulse" />
            ) : (
              <input
                id="org-settings-conversions-weekly"
                type="number"
                min={0}
                value={conversionsWeekly}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onConversionsWeeklyChange(Number.isNaN(v) ? 0 : Math.max(0, v));
                }}
                disabled={isSuperTenant}
                className={SETTINGS_CONTROL_CLASS}
              />
            )}
          </SettingsField>
          <SettingsField
            label={t("iam.orgDrawer.settings.monthlyConversionsShortLabel", "Monthly conv.")}
            htmlFor="org-settings-conversions-monthly"
          >
            {isLoadingAdminSettings ? (
              <div className="h-9 rounded-md border border-border bg-muted/40 animate-pulse" />
            ) : (
              <input
                id="org-settings-conversions-monthly"
                type="number"
                min={0}
                value={conversionsMonthly}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onConversionsMonthlyChange(Number.isNaN(v) ? 0 : Math.max(0, v));
                }}
                disabled={isSuperTenant}
                className={SETTINGS_CONTROL_CLASS}
              />
            )}
          </SettingsField>
        </div>
        {conversionsSaved && (
          <p className="-mt-2 text-[11px] text-success-subtle-foreground">
            {t("iam.orgDrawer.settings.conversionsSaved", "Saved")}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle pb-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    data-testid="org-bq-sync-trigger"
                    disabled={isSyncDisabled}
                    onClick={onTriggerSync}
                  >
                    {isBusy ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
                    {syncButtonLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              {partitionIsEmpty && !isSuperTenant && (
                <TooltipContent side="bottom">
                  {t("iam.orgDrawer.settings.setPartitionFirst", "Set a Customer Partition first")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {lastSynced?.finishedAt && !showFailure ? (
            <p className="flex min-w-0 flex-1 items-center gap-1 text-[11px]" data-testid="org-bq-last-synced">
              <Check className="size-3 shrink-0 text-success-subtle-foreground" aria-hidden />
              <span className="shrink-0 text-success-subtle-foreground">
                {t("iam.orgDrawer.settings.bqSync.syncedAt", "Synced {{date}}", {
                  date: formatRunDate(lastSynced.finishedAt),
                })}
              </span>
              <span className="truncate text-muted-foreground">
                {"· "}
                {t("iam.orgDrawer.settings.bqSync.productTypeCount", {
                  count: productTypes.length,
                  defaultValue_one: "{{count}} product type",
                  defaultValue_other: "{{count}} product types",
                })}
                {", "}
                {t("iam.orgDrawer.settings.bqSync.productCount", {
                  count: skuProducts.length,
                  defaultValue_one: "{{count}} product",
                  defaultValue_other: "{{count}} products",
                })}
              </span>
            </p>
          ) : (
            <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">
              {t("iam.orgDrawer.settings.bqSync.runsWithPartition", "Runs now with the partition above")}
            </p>
          )}
          <Link
            to="/admin"
            search={{ tab: "scheduledJobs", view: "actions", sortBy: "publishedAt", sortOrder: "desc" }}
            className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="org-bq-scheduled-jobs-link"
          >
            {t("iam.orgDrawer.settings.bqSync.dailySyncInScheduledJobs", "Daily sync · Scheduled jobs")}
            <ArrowUpRight className="size-3" aria-hidden />
          </Link>
          <div className="flex w-full items-center gap-2">
            <Switch
              id="org-bq-failure-slack-alert"
              data-testid="org-bq-failure-slack-alert"
              checked={isFailureSlackAlertEnabled}
              onCheckedChange={onFailureSlackAlertEnabledChange}
              disabled={isSuperTenant || isLoadingAdminSettings}
            />
            <label htmlFor="org-bq-failure-slack-alert" className="text-xs font-medium text-foreground">
              {t("iam.orgDrawer.settings.bqSync.failureSlackAlert", "Alert #nexus-admin-alerts when a sync fails")}
            </label>
            <span className="text-[11px] text-muted-foreground">
              {t("iam.orgDrawer.settings.bqSync.failureSlackAlertHint", "Production only")}
            </span>
          </div>
        </div>

        <section className="flex flex-col gap-2" data-testid="org-bq-sync-history">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-medium text-foreground">
              {t("iam.orgDrawer.settings.bqSync.recentSyncs", "Recent syncs")}
            </h3>
            {!isLoadingSyncRuns && syncRunsTotalCount > syncRuns.length && (
              <span className="text-[11px] text-muted-foreground">
                {t("iam.orgDrawer.settings.bqSync.lastNOfTotal", "Last {{shown}} of {{total}}", {
                  shown: syncRuns.length,
                  total: syncRunsTotalCount,
                })}
              </span>
            )}
          </div>
          {isLoadingSyncRuns ? (
            <div className="h-16 rounded-md border border-border bg-muted/40 animate-pulse" />
          ) : syncRuns.length === 0 ? (
            <p
              className="rounded-md border border-dashed border-border px-3 py-3 text-center text-[11px] text-muted-foreground"
              data-testid="org-bq-sync-history-empty"
            >
              {t(
                "iam.orgDrawer.settings.bqSync.noRunsYet",
                "No syncs recorded yet — runs appear here after the next sync.",
              )}
            </p>
          ) : (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-md border border-border">
              {syncRuns.map((run) => {
                const duration = formatSyncDurationMs(run.startedAt, run.finishedAt);
                const triggeredBy = run.triggeredByUserId ? memberNameById.get(run.triggeredByUserId) : undefined;
                return (
                  <li
                    key={run.id}
                    className={cn(
                      "grid grid-cols-[120px_minmax(0,130px)_minmax(0,1fr)_44px] items-center gap-3 px-3 py-2 text-[11px] leading-none",
                      run.status === "FAILED" && "bg-destructive/[0.03]",
                    )}
                  >
                    <span className="font-mono text-foreground">{formatRunDate(run.startedAt)}</span>
                    <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
                      {run.trigger === "SCHEDULED" ? (
                        <Clock className="size-3 shrink-0" aria-hidden />
                      ) : (
                        <User className="size-3 shrink-0" aria-hidden />
                      )}
                      <span className="truncate">
                        {run.trigger === "SCHEDULED"
                          ? t("iam.orgDrawer.settings.bqSync.triggerScheduledLabel", "Scheduled")
                          : (triggeredBy ?? t("iam.orgDrawer.settings.bqSync.triggerManualLabel", "Manual"))}
                      </span>
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <StatusBadge status={run.status} />
                      <span className="truncate text-muted-foreground">{summarizeSyncRun(t, run)}</span>
                    </span>
                    <span className="text-right font-mono text-muted-foreground">{duration ?? ""}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {isLoadingAdminSettings ? (
          <div className="h-10 rounded-md border border-border bg-muted/40 animate-pulse" />
        ) : showFailure && bqCatalogAsOfAt ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="org-bq-catalog-as-of">
            <Database className="size-3 text-muted-foreground" aria-hidden />
            <span className="pr-1 text-[11px] leading-none text-muted-foreground">
              {t("iam.orgDrawer.settings.bqSync.stillUsingDataFromLabel", "Still using data from {{date}}:", {
                date: new Date(bqCatalogAsOfAt).toLocaleDateString(undefined, CATALOG_DATE_FORMAT),
              })}
            </span>
            {productTypes.map((p) => (
              <span key={`type-${p.name}`} className={CHIP_CLASS}>
                {toTitleCase(p.name)}
              </span>
            ))}
            {focusedSkus.map((p) => (
              <span key={`sku-${p.name}`} className={CHIP_CLASS}>
                {p.name}
              </span>
            ))}
            {unfocusedSkuCount > 0 && <MoreChip count={unfocusedSkuCount} />}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <section className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <Database className="size-3 text-muted-foreground" aria-hidden />
                <h3 className="text-[11px] font-medium leading-none text-muted-foreground">
                  {t("iam.orgDrawer.settings.tenantProductTypesLabel", "Product types")}
                </h3>
                <span className="rounded-sm bg-border-subtle px-1 py-[3px] font-mono text-[9px] uppercase leading-none text-muted-foreground">
                  {t("iam.orgDrawer.settings.bqSync.fromBq", "From BQ")}
                </span>
              </div>
              {productTypes.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {productTypes.map((p) => (
                    <span key={`type-${p.name}`} className={CHIP_CLASS}>
                      {toTitleCase(p.name)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] italic text-muted-foreground">
                  {t("iam.orgDrawer.settings.noProductsSynced", "No products synced yet")}
                </p>
              )}
            </section>
            {skuProducts.length > 0 && (
              <section className="flex flex-col gap-1.5">
                <h3 className="text-[11px] font-medium leading-none text-muted-foreground">
                  {t("iam.orgDrawer.settings.tenantProductsLabel", "Tenant products")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {focusedSkus.map((p) => (
                    <span key={`sku-${p.name}`} className={CHIP_CLASS}>
                      {p.name}
                    </span>
                  ))}
                  {unfocusedSkuCount > 0 && <MoreChip count={unfocusedSkuCount} />}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </SettingsRow>
  );
}
